import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import {
  buildOrdenProduccion,
  consolidarItemsSalida,
  expandInsumosReceta,
  formatMoney,
  getOrdersMock,
  MOCK_ZONAS,
  normalizeOrder,
  seedOrdersMock,
  setOrdersMock,
} from './mockOperaciones';
import { countCocinasActivas } from '../Configuracion/cocinasStorage';

const EspacioProduccion = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [consolidadoPedido, setConsolidadoPedido] = useState(null);

  const refresh = useCallback(() => {
    setOrders(getOrdersMock());
  }, []);

  const cargarDemo = () => {
    const seeded = seedOrdersMock();
    setOrders(seeded);
    setToast({
      isOpen: true,
      message: 'Se cargaron más datos de prueba para producción.',
      type: 'success',
    });
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  const zonesMap = useMemo(() => {
    const m = {};
    MOCK_ZONAS.forEach((z) => {
      m[z.id] = z;
    });
    return m;
  }, []);

  const pedidosAbiertos = useMemo(
    () => orders.filter((o) => o.estado === 'abierto' && (o.items || []).length > 0).map((o) => normalizeOrder(o)),
    [orders]
  );

  const pendientes = useMemo(
    () => pedidosAbiertos.filter((o) => o.produccionEstado === 'pendiente'),
    [pedidosAbiertos]
  );

  const enCocina = useMemo(
    () => pedidosAbiertos.filter((o) => o.produccionEstado === 'en_cocina'),
    [pedidosAbiertos]
  );

  const entregaPendiente = useMemo(
    () => pedidosAbiertos.filter((o) => o.produccionEstado === 'entrega_pendiente'),
    [pedidosAbiertos]
  );

  const cocinasActivas = countCocinasActivas();
  const cupoEnCocina = enCocina.length;
  const cocinaLlena = cocinasActivas > 0 && cupoEnCocina >= cocinasActivas;
  const sinCocinasActivas = cocinasActivas === 0;

  const persist = (next) => {
    setOrders(next);
    setOrdersMock(next);
  };

  const pasarACocina = (order) => {
    if (order.produccionEstado !== 'pendiente') return;
    if (sinCocinasActivas) {
      setToast({
        isOpen: true,
        message: 'No hay cocinas activas. Activa al menos una estación en Configuración → Cocinas.',
        type: 'error',
      });
      return;
    }
    if (cocinaLlena) {
      setToast({
        isOpen: true,
        message: `Capacidad de cocina llena: máximo ${cocinasActivas} pedido(s) en simultáneo (${cocinasActivas} cocina(s) activa(s)).`,
        type: 'warning',
      });
      return;
    }
    const op = buildOrdenProduccion(order);
    const next = orders.map((o) =>
      o.id === order.id
        ? normalizeOrder({
            ...o,
            produccionEstado: 'en_cocina',
            ordenProduccion: op,
          })
        : o
    );
    persist(next);
    setToast({
      isOpen: true,
      message: `Orden de producción ${op.id} generada y enviada a cocina.`,
      type: 'success',
    });
  };

  const marcarTerminado = (order) => {
    if (order.produccionEstado !== 'en_cocina' || !order.ordenProduccion) return;
    const fechaTerminadoCocina = new Date().toISOString();
    const next = orders.map((o) =>
      o.id === order.id
        ? normalizeOrder({
            ...o,
            produccionEstado: 'entrega_pendiente',
            ordenProduccion: {
              ...o.ordenProduccion,
              fechaTerminadoCocina,
            },
          })
        : o
    );
    persist(next);
    setToast({
      isOpen: true,
      message: 'Cocina terminada: el pedido queda en entrega pendiente.',
      type: 'success',
    });
  };

  const labelMesa = (o) => (o.tipo === 'bar' ? 'Barra' : `Mesa ${o.mesaId}`);

  const totalPedido = (o) =>
    (o.items || []).reduce((s, it) => s + Number(it.precio || 0) * Number(it.cantidad || 0), 0);

  const irPedido = (o) => {
    if (o.tipo === 'bar') navigate(`/restaurantes/operaciones/bar/${o.zoneId}`);
    else navigate(`/restaurantes/operaciones/mesa/${o.zoneId}/${o.mesaId}`);
  };

  const abrirConsolidado = (order) => {
    if (!order?.ordenProduccion) return;
    setConsolidadoPedido(order);
  };

  const filasConsolidado = useMemo(() => {
    if (!consolidadoPedido?.ordenProduccion?.items) return [];
    return consolidarItemsSalida(consolidadoPedido.ordenProduccion.items);
  }, [consolidadoPedido]);

  const totalConsolidado = useMemo(
    () => filasConsolidado.reduce((s, it) => s + Number(it.subtotal || 0), 0),
    [filasConsolidado]
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Espacio de producción</h1>
          <p className="text-gray-600 text-sm mt-1 max-w-2xl">
            Recibe los pedidos abiertos, envíalos a cocina (se genera la <span className="font-medium">orden de producción</span>{' '}
            con el detalle de lo solicitado). Al terminar de cocinar, marca <span className="font-medium">Terminado</span> para
            pasar el pedido a <span className="font-medium">entrega pendiente</span> (listo para llevar a mesa o entregar).
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
            onClick={() => navigate('/restaurantes/operaciones/pedidos')}
          >
            Ir a pedidos
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-amber-100 text-amber-900 rounded-md text-sm font-medium hover:bg-amber-200"
            onClick={cargarDemo}
          >
            Cargar datos demo
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700"
            onClick={refresh}
          >
            Actualizar
          </button>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3 text-sm text-teal-950 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <span className="font-semibold">Capacidad según cocinas activas:</span>{' '}
          <span className="tabular-nums">
            {cupoEnCocina} / {cocinasActivas || 0}
          </span>{' '}
          pedidos en cocina al mismo tiempo
          {sinCocinasActivas ? (
            <span className="block mt-1 text-amber-900 font-medium">
              No hay cocinas activas: no se puede enviar pedidos a cocina hasta configurar al menos una.
            </span>
          ) : cocinaLlena ? (
            <span className="block mt-1 text-amber-900 font-medium">
              Cocina al máximo: libera un pedido (termina cocina) o activa más cocinas en configuración.
            </span>
          ) : null}
        </div>
        <Link
          to="/restaurantes/configuraciones/cocinas"
          className="shrink-0 text-teal-800 font-medium hover:underline text-right"
        >
          Configuración de cocinas →
        </Link>
      </div>

      {/* Pendientes */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-800 text-sm font-bold">
            {pendientes.length}
          </span>
          Pendientes de enviar a cocina
        </h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Pedido</th>
                  <th className="text-left px-4 py-3 font-medium">Zona</th>
                  <th className="text-left px-4 py-3 font-medium">Mesa / Barra</th>
                  <th className="text-left px-4 py-3 font-medium">Mozo</th>
                  <th className="text-left px-4 py-3 font-medium">Total</th>
                  <th className="text-right px-4 py-3 font-medium w-[220px]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pendientes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No hay pedidos pendientes de cocina.
                    </td>
                  </tr>
                ) : (
                  pendientes.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{o.id}</td>
                      <td className="px-4 py-3">{zonesMap[o.zoneId]?.nombreZona || o.zoneId}</td>
                      <td className="px-4 py-3">{labelMesa(o)}</td>
                      <td className="px-4 py-3">{o.mozo || '—'}</td>
                      <td className="px-4 py-3 font-medium">S/ {totalPedido(o).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2 flex-wrap">
                          <button
                            type="button"
                            className="text-sm text-gray-700 hover:text-gray-900 font-medium"
                            onClick={() => irPedido(o)}
                          >
                            Ver pedido
                          </button>
                          <button
                            type="button"
                            disabled={sinCocinasActivas || cocinaLlena}
                            className="text-sm px-3 py-1.5 rounded-md bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-600"
                            onClick={() => pasarACocina(o)}
                          >
                            Pasar a cocina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* En cocina */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-teal-800 text-sm font-bold">
            {enCocina.length}
          </span>
          En cocina — orden de producción
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          Cupo usado según cocinas activas:{' '}
          <span className="font-medium tabular-nums">
            {cupoEnCocina} / {cocinasActivas || 0}
          </span>
          {cocinasActivas > 0 ? (
            <span className="text-gray-500"> (máx. {cocinasActivas} pedido(s) en paralelo)</span>
          ) : null}
        </p>
        {enCocina.length === 0 ? (
          <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-4 py-8 text-center">
            No hay pedidos en cocina.
          </div>
        ) : (
          <div className="space-y-4">
            {enCocina.map((o) => {
              const op = o.ordenProduccion;
              if (!op) return null;
              return (
                <div key={o.id} className="bg-white rounded-lg border border-teal-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-teal-50 border-b border-teal-100 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-xs text-teal-700 font-medium">Orden de producción</div>
                      <div className="text-lg font-bold text-teal-900">{op.id}</div>
                    </div>
                    <div className="text-xs text-gray-600">
                      Enviado: {new Date(op.fechaEnvioCocina).toLocaleString('es-PE')}
                    </div>
                  </div>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm border-b border-gray-100">
                    <div>
                      <span className="text-gray-500">Pedido:</span>{' '}
                      <span className="font-mono text-gray-800">{op.pedidoId}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Zona:</span> <span className="font-medium">{op.zonaNombre}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Mesa / barra:</span>{' '}
                      <span className="font-medium">{op.mesaLabel}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Mozo:</span> <span className="font-medium">{op.mozo || '—'}</span>
                    </div>
                    {op.observacionPedido ? (
                      <div className="sm:col-span-2">
                        <span className="text-gray-500">Observación:</span>{' '}
                        <span className="text-gray-800">{op.observacionPedido}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Detalle a producir</div>
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                          <th className="py-2 pr-2">Ítem</th>
                          <th className="py-2 pr-2 w-20">Cant.</th>
                          <th className="py-2 pr-2">P. unit.</th>
                          <th className="py-2 text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(op.items || []).map((it, idx) => (
                          <tr key={`${it.itemId}-${idx}`} className="border-b border-gray-50 last:border-0">
                            <td className="py-2 pr-2 font-medium text-gray-900">{it.nombre}</td>
                            <td className="py-2 pr-2">{it.cantidad}</td>
                            <td className="py-2 pr-2">S/ {formatMoney(it.precio)}</td>
                            <td className="py-2 text-right font-medium">S/ {formatMoney(it.subtotal ?? it.precio * it.cantidad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
                      <div className="text-base font-bold text-gray-900">
                        Total: S/ {formatMoney(op.total)}
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-teal-800 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100"
                      onClick={() => abrirConsolidado(o)}
                    >
                      Consolidado de salida
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100"
                      onClick={() => irPedido(o)}
                    >
                      Ver pedido
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                      onClick={() => marcarTerminado(o)}
                    >
                      Terminado — entrega pendiente
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Entrega pendiente */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-900 text-sm font-bold">
            {entregaPendiente.length}
          </span>
          Entrega pendiente
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          Pedidos ya cocinados: pendientes de servir o entregar al cliente.
        </p>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Orden prod.</th>
                  <th className="text-left px-4 py-3 font-medium">Pedido</th>
                  <th className="text-left px-4 py-3 font-medium">Ubicación</th>
                  <th className="text-left px-4 py-3 font-medium">Terminado cocina</th>
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                  <th className="text-right px-4 py-3 font-medium">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entregaPendiente.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No hay pedidos con entrega pendiente.
                    </td>
                  </tr>
                ) : (
                  entregaPendiente.map((o) => {
                    const op = o.ordenProduccion;
                    return (
                      <tr key={o.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-teal-800 font-medium">{op?.id || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{o.id}</td>
                        <td className="px-4 py-3">
                          {zonesMap[o.zoneId]?.nombreZona || o.zoneId} · {labelMesa(o)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {op?.fechaTerminadoCocina
                            ? new Date(op.fechaTerminadoCocina).toLocaleString('es-PE')
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                            Entrega pendiente
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="text-sm text-teal-700 hover:text-teal-900 font-medium"
                            onClick={() => irPedido(o)}
                          >
                            Ver pedido
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {consolidadoPedido?.ordenProduccion ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setConsolidadoPedido(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Consolidado de salida</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Productos a retirar / despachar: cantidades y costos por ítem
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConsolidadoPedido(null)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 shrink-0"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="px-4 py-3 border-b border-gray-100 text-sm space-y-1">
              <div>
                <span className="text-gray-500">Orden prod.:</span>{' '}
                <span className="font-mono font-semibold text-teal-800">{consolidadoPedido.ordenProduccion.id}</span>
              </div>
              <div>
                <span className="text-gray-500">Pedido:</span>{' '}
                <span className="font-mono">{consolidadoPedido.ordenProduccion.pedidoId}</span>
                {' · '}
                <span className="font-medium">{consolidadoPedido.ordenProduccion.zonaNombre}</span>
                {' · '}
                <span className="font-medium">{consolidadoPedido.ordenProduccion.mesaLabel}</span>
              </div>
              {consolidadoPedido.ordenProduccion.observacionPedido ? (
                <div className="text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 text-xs">
                  <span className="font-medium">Obs.:</span> {consolidadoPedido.ordenProduccion.observacionPedido}
                </div>
              ) : null}
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="text-xs font-semibold text-gray-600 mb-2">
                Detalle por producto (cantidades consolidadas y costos)
              </div>
              <div className="overflow-x-auto rounded-md border border-gray-100">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 bg-gray-50 border-b border-gray-200">
                      <th className="py-2.5 px-3 font-medium">Producto</th>
                      <th className="py-2.5 px-3 font-medium w-20 text-right">Cant.</th>
                      <th className="py-2.5 px-3 font-medium w-28 text-right">Costo unit.</th>
                      <th className="py-2.5 px-3 font-medium w-28 text-right">Costo línea</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasConsolidado.map((it) => {
                      const insumos = expandInsumosReceta(it.itemId, it.cantidad);
                      return (
                        <React.Fragment key={String(it.itemId || it.nombre)}>
                          <tr className="border-b border-gray-100">
                            <td className="py-2.5 px-3 font-medium text-gray-900">{it.nombre}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-gray-800">{it.cantidad}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-gray-700">
                              S/ {formatMoney(it.precio)}
                            </td>
                            <td className="py-2.5 px-3 text-right font-semibold text-teal-900 tabular-nums">
                              S/ {formatMoney(it.subtotal)}
                            </td>
                          </tr>
                          {insumos.length > 0 ? (
                            <tr className="bg-slate-50/90">
                              <td colSpan={4} className="p-0 border-b border-gray-100">
                                <div className="px-3 py-2 text-xs font-semibold text-slate-700">
                                  Productos de la receta (insumos a retirar / usar)
                                </div>
                                <div className="px-3 pb-3 overflow-x-auto">
                                  <table className="min-w-full text-xs text-slate-800">
                                    <thead>
                                      <tr className="text-slate-600 border-b border-slate-200">
                                        <th className="py-1.5 pr-2 text-left font-medium">Insumo</th>
                                        <th className="py-1.5 pr-2 text-right font-medium w-24">Cantidad</th>
                                        <th className="py-1.5 pr-2 text-left font-medium w-16">Und.</th>
                                        <th className="py-1.5 pr-2 text-right font-medium w-24">Costo unit.</th>
                                        <th className="py-1.5 text-right font-medium w-24">Costo</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {insumos.map((ins, idx) => (
                                        <tr
                                          key={`${String(it.itemId)}-${idx}-${ins.nombre}`}
                                          className="border-b border-slate-100/80 last:border-0"
                                        >
                                          <td className="py-1.5 pr-2">{ins.nombre}</td>
                                          <td className="py-1.5 pr-2 text-right tabular-nums">{ins.cantidad}</td>
                                          <td className="py-1.5 pr-2">{ins.unidad}</td>
                                          <td className="py-1.5 pr-2 text-right tabular-nums">
                                            S/ {formatMoney(ins.costoUnit)}
                                          </td>
                                          <td className="py-1.5 text-right font-medium tabular-nums">
                                            S/ {formatMoney(ins.costoLinea)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr className="bg-gray-50/50">
                              <td
                                colSpan={4}
                                className="py-2 px-3 text-xs text-gray-500 border-b border-gray-100 italic"
                              >
                                Sin insumos de receta configurados para este ítem (demo).
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
                <div className="text-base font-bold text-gray-900">
                  Costo total: S/ {formatMoney(totalConsolidado)}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setConsolidadoPedido(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EspacioProduccion;
